/**
 * Resource amounts are always whole numbers, rounded UP — a plunder of 12.2 is shown as 13, never 12 and never
 * "12.2" — so the victim is never told they lost less than they did. Negative / NaN clamp to 0.
 */
export function ceilInt(n: number): number {
  return Number.isFinite(n) ? Math.max(0, Math.ceil(n)) : 0;
}

/** `ceilInt` with thousands separators, matching the rest of the UI ("1,234"). */
export function formatCeil(n: number): string {
  return ceilInt(n).toLocaleString('en-US');
}

/** Sum of a resource record where every entry is rounded up first, so a total always equals the sum of the rows shown. */
export function sumCeil(record: Partial<Record<string, number>> | undefined): number {
  return Object.values(record ?? {}).reduce<number>((total, v) => total + ceilInt(v ?? 0), 0);
}

/**
 * Short numbers for badges: under a thousand as is; thousands as "K", millions as "M", ALWAYS with two decimals and
 * rounded UP (1,250 -> "1.25K", 12,301 -> "12.31K", 1,000 -> "1.00K"). Rounding up means a badge never claims less
 * than the real figure.
 */
export function formatShort(n: number): string {
  const v = Math.max(0, Math.floor(n));
  if (v < 1000) return String(v);
  const k = Math.ceil(v / 10) / 100; // thousands, rounded up to 2 decimals
  if (v < 1_000_000 && k < 1000) return `${k.toFixed(2)}K`;
  return `${(Math.ceil(v / 10_000) / 100).toFixed(2)}M`;
}
